import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import CONSTANTS from "core/constants";
import type { Slot, TemplateName } from "core/types";
import { SlotStatus } from "core/types";
import { useHover } from "hooks";
import { useTemplates } from "hooks/useTemplates";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { eventDragStartStore } from "../../hooks/eventDragStartStore";
import SlotColorStatus from "../../calendar/slotColorStatus";
import {
  getSlotBackgroundColor as _getSlotBackgroundColor,
  isSlotCanceled,
} from "./slotColor";
import {
  Container,
  HoverStatus,
  StatusText,
  TimeContainer,
} from "./slotComponent.styles";
import { usePatientUserIdFromUrl } from "hooks/usePatientUserIdFromUrl";

const SlotComponent = ({ event }: { event: Slot }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const eventDragStart = useSyncExternalStore(
    eventDragStartStore.subscribe,
    eventDragStartStore.get,
  );
  const [hoverRef, isHovered] = useHover();
  const { data: templates } = useTemplates();
  const demo = useDemo();
  const [patientUserIdFromSearchParams] = usePatientUserIdFromUrl();

  const getSlotTemplate = useCallback(
    (templateId: number) => {
      const template = templates.find((temp) => temp.templateId === templateId);
      return template;
    },
    [templates],
  );

  const template = useMemo(() => {
    return getSlotTemplate(event.templateId);
  }, [event.templateId, getSlotTemplate]);

  const api = useMindlerApi();
  const { data: isCouplesTherapyRevisit } = useQuery(
    api.slots.isRevisit.queryOptions(
      {
        slotId: event.slotId,
      },
      {
        enabled:
          template?.name === CONSTANTS.SLOT_TEMPLATES.COUPLES_THERAPY &&
          event.status !== CONSTANTS.BOOKING_STATUS.OPEN,
        refetchOnMount: false,
      },
    ),
  );

  const getSlotBackgroundColor = useCallback(() => {
    return _getSlotBackgroundColor({
      templateName: template?.name as TemplateName,
      slotStatus: event.status as SlotStatus,
      isCouplesTherapyRevisit,
    });
  }, [event.status, template?.name, isCouplesTherapyRevisit]);

  // Fix to get updated react big calendar label on each vertical drag
  /* eslint-disable react-hooks/refs */
  const currentLabel = event?.isDurationOneHour
    ? containerRef?.current?.parentNode?.parentNode?.childNodes[0]?.textContent
    : containerRef?.current?.parentNode?.parentNode?.childNodes[0]?.textContent?.slice(
        0,
        -8,
      );
  /* eslint-enable react-hooks/refs */

  const isCanceled = isSlotCanceled(event.status as SlotStatus);
  return (
    <Container
      isSlotValidForPatientSelected={
        !!(
          event?.status === CONSTANTS.BOOKING_STATUS.OPEN &&
          template?.isMeeting &&
          patientUserIdFromSearchParams
        )
      }
      color={getSlotBackgroundColor()}
      {...(event?.slotId && { "data-testid": "slot-color-status-div" })}
      isDurationOneHour={event?.isDurationOneHour}
      isCanceled={isCanceled}
      ref={containerRef}
    >
      <TimeContainer isDurationOneHour={event?.isDurationOneHour}>
        {isHovered && event?.showStatusOnHover === true && (
          <HoverStatus data-testid="slot-color-status-div-popup">
            {event?.translatedHoverText}
          </HoverStatus>
        )}
        {isHovered && event?.showStatusOnHover === false && demo && (
          <HoverStatus data-testid="slot-color-status-div-popup">
            {`${CONSTANTS.DEMO}`}
          </HoverStatus>
        )}
        {isHovered && event?.showStatusOnHover === false && !demo && (
          <HoverStatus data-testid="slot-color-status-div-popup">
            {`${event.firstName} ${event.lastName}`}
          </HoverStatus>
        )}
        <>
          {event.showColorStatus && (
            <SlotColorStatus
              event={event}
              templateName={template?.name ?? ""}
              ref={hoverRef as React.RefObject<HTMLDivElement>}
            />
          )}
        </>
        <Typography insensitive variant="body-x-small-bold">
          {/* eslint-disable-next-line react-hooks/refs */}
          {currentLabel && currentLabel !== "" && eventDragStart !== null
            ? currentLabel
            : event?.slotTime}
        </Typography>
      </TimeContainer>
      <StatusText insensitive variant="body-x-small">
        {event?.formattedSlotTitle}
      </StatusText>
    </Container>
  );
};

export default SlotComponent;
